import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, vShow as _vShow, withDirectives as _withDirectives, unref as _unref, toDisplayString as _toDisplayString, Teleport as _Teleport, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "container" }
const _hoisted_2 = {
  key: 0,
  class: "wind-radar"
}
const _hoisted_3 = { class: "mainchar" }
const _hoisted_4 = /*#__PURE__*/_createElementVNode("div", { id: "x_highlight" }, null, -1)
const _hoisted_5 = /*#__PURE__*/_createElementVNode("canvas", { id: "myManySpotChart" }, null, -1)
const _hoisted_6 = [
  _hoisted_4,
  _hoisted_5
]
const _hoisted_7 = { class: "minimap_wrapper" }
const _hoisted_8 = { id: "graphToGraphLine" }
const _hoisted_9 = /*#__PURE__*/_createElementVNode("div", { id: "dot" }, null, -1)
const _hoisted_10 = [
  _hoisted_9
]
const _hoisted_11 = /*#__PURE__*/_createElementVNode("canvas", {
  id: "minimap",
  class: "chart_timeline_canvas",
  height: "40"
}, null, -1)
const _hoisted_12 = { key: 0 }
const _hoisted_13 = { class: "minimap-range-min range-item" }
const _hoisted_14 = { class: "minimap-range-max range-item" }
const _hoisted_15 = { class: "minimap-range-center range-item" }
const _hoisted_16 = /*#__PURE__*/_createElementVNode("div", { id: "tooltip-div" }, null, -1)
const _hoisted_17 = { class: "chart-print-header-title" }
const _hoisted_18 = { class: "chart-print-header-name" }
const _hoisted_19 = { class: "chart-print-header-range" }

import { ref, computed, onMounted, watch, PropType, nextTick } from 'vue'
import { useRoute } from 'vue-router'
import { ChartData, ChartDataset } from 'chart.js/auto'
import 'chartjs-adapter-moment'
import moment from 'moment'
import { throttle, debounce, camelCase } from 'lodash'
import { useEventListener } from '@vueuse/core'
import { StationListItem } from '@/models/InitData'
import { useChartConfigStore } from '@/stores/chartConfigStore'
import { useInitDataStore } from '@/stores/initDataStore'
import { XRangeType } from '@/lib/graph/types/initResult'
import { HistoryDataService } from '@/lib/graph/historyDataService'
import { GraphData, GraphDatum, GraphTypeField } from '@/lib/graph/types/graphData'
import { XRange } from '@/lib/graph/types/initResult'
import IWSMainChart from '@/lib/graph/IWSChart/IWSMainChart'
import IWSMiniChart, { OVERLAP_DIRECTION } from '@/lib/graph/IWSChart/IWSMiniChart'
import { Option } from '@/components/common/MultiSelectModal.vue'
import WindRadar from '@/components/graph/WindRadar/WindRadar.vue'
import { setTitle as resetTitle } from '@/router/index'

type Scale = {
  min: number
  max: number
  autoStep: number
}


export default /*#__PURE__*/_defineComponent({
  props: {
  center: {
    type: Number,
    required: true,
  },
  field: {
    type: Object as PropType<GraphTypeField>,
    required: true,
  },
  stations: {
    type: Array as PropType<StationListItem[]>,
    required: true,
  },
  fields: {
    type: Array as PropType<Option[]>,
    required: false,
  },
  modal: {
    type: Boolean,
    required: true,
  },
  sclaes: {
    type: Object as PropType<{ unit: string; scale: { min: number; max: number } }>,
    required: false,
  },
  showWindRateCount: {
    type: Boolean,
    required: false,
  },
},
  emits: ['update:center', 'reset'],
  setup(__props, { emit }) {

const props = __props




let chart: IWSMainChart
let minimapChart: IWSMiniChart

const configStore = useChartConfigStore()
const initDataStore = useInitDataStore()
const route = useRoute()
const initData = initDataStore.initData
const center = ref<number>(props.center)
const initialized = ref(false)
const mainRange = ref<{ start: number; end: number }>()
const service = new HistoryDataService()
const isWeather = computed(() => props.field.stationId !== undefined)
const reportType = computed(() => props.field.reportType || (props.field.items && props.field.items[0].type))
const minimapStartText = computed(() => {
  if (!chart) {
    return ''
  }

  return minimapChart?.getTimeString(center.value, 'start', reportType.value)
})
const minimapEndText = computed(() => {
  if (!chart) {
    return ''
  }

  return minimapChart?.getTimeString(center.value, 'end', reportType.value)
})
const fieldRangeText = computed(() => {
  const xRange = reportType.value

  switch (xRange) {
    case XRange.HOUR:
      return '時報'
    case XRange.DAY:
      return '日報'
    case XRange.MONTH:
      return '月報'
    case XRange.YEAR:
      return '年報'
    default:
      return 'else'
  }
})
const fieldNameText = computed(() => {
  const selectedId = camelCase(props.field.selectedId)
  const field = initData.fieldList[selectedId]

  if (!field) {
    return ''
  }

  const graphNames = field.graphNames || {}
  const key = camelCase(`${field.type}_${props.field.reportType}ly`)
  const name = graphNames[key] || field.name

  return name
})
const setTitle = () => {
  const range = getMainRange(center.value)
  let format = 'MM月DD日HH時mm分'

  if (!range) {
    return
  }

  switch (reportType.value) {
    case 'month':
      format = 'YYYY年MM月DD日'
      break
    case 'year':
      format = 'YYYY年MM月'
      break
  }

  const startText = moment(range.start).format(format)
  const endText = moment(range.end).format(format)

  window.document.title = `${fieldRangeText.value}グラフ${fieldNameText.value}_${startText}_${endText}`
}

const currentStation = computed(() => {
  const { stationId } = props.field

  if (stationId === undefined) {
    return
  }

  return initDataStore.findStation(stationId)
})

const colors = computed(() => {
  return isWeather.value ? props.fields?.map(f => f.color || '') || [] : props.stations.map(s => s.color || '')
})

const currentFields = computed(() => {
  const field = props.field.selectedId
  let fields = [field] as string[]

  if (!field && props.field.stationId !== undefined) {
    if (currentStation.value === undefined) {
      return
    }

    fields = props.fields?.map(f => f.value) || []
  }

  return fields
})

const currentStations = computed(() => {
  const field = props.field.selectedId
  let stations = props.stations.map(station => station.stationId)

  if (!field && props.field.stationId !== undefined) {
    if (currentStation.value === undefined) {
      return
    }

    stations = [props.field.stationId]
  }

  return stations
})

// 印刷イベント
useEventListener(window, 'beforeprint', () => {
  if (chart.chartjs === undefined) {
    return
  }

  chart.chartjs.resize(1200, 600)
  adjustWindow()
  setTitle()
})
useEventListener(window, 'afterprint', () => {
  if (chart.chartjs === undefined) {
    return
  }

  chart.chartjs.resize()
  adjustWindow()
  resetTitle(route)
})

// リサイズイベント
useEventListener(
  window,
  'resize',
  debounce(() => adjustWindow(), 100)
)

onMounted(async () => {
  await initialize()
  initialized.value = true
})

const initialize = async () => {
  const xRangeType = reportType.value
  const fields = currentFields.value
  const spots = currentStations.value

  if (xRangeType === undefined) {
    return
  }

  if (!spots || spots.length === 0 || fields?.every(f => !f)) {
    return
  }

  const marginRange = IWSMiniChart.getMarginRange(center.value, xRangeType)
  const _graphData = await service.fetch(
    marginRange.start,
    marginRange.end,
    spots,
    fields,
    xRangeType,
    colors.value || []
  )
  const _mainDatasets = setMainChartData(_graphData.data)
  const mainDatasets = {
    datasets: _mainDatasets.data,
    labels: _mainDatasets.labels,
  }
  const graphData = {
    datasets: _graphData.data,
    labels: _graphData.labels,
  }

  drawMainChart(mainDatasets, xRangeType)
  drawMinimapChart(graphData, xRangeType)

  chart.updateZoomScale(center.value)

  setTimeout(() => {
    const _center = minimapChart.getCenter()

    _center && chart.updateZoomScale(_center)
    setMainRangeVisual()
    adjustWindow()
  }, 100)
}

/**
 * グラフ表示の中心位置centerの変化に伴って表示領域と保持データの更新
 */
const updateGraph = async () => {
  try {
    if (!minimapChart || !minimapChart.chartjs) {
      return
    }

    const overlapDirection = minimapChart.overlapRange(center.value)

    // 未描画部分がある場合のみAPIからデータ取得
    if (overlapDirection !== OVERLAP_DIRECTION.OVERLAP) {
      const min = minimapChart.min?.x
      const max = minimapChart.max?.x

      if (!min || !max) {
        return
      }

      const fields = currentFields.value
      const spots = currentStations.value
      const range = // 左右どちらに空白があるかで取得するrangeを分岐
        overlapDirection === OVERLAP_DIRECTION.LEFT
          ? IWSMiniChart.getMarginUnderRange(min, reportType.value as string)
          : IWSMiniChart.getMarginOverRange(max, reportType.value as string)
      const addData = await service.fetch(
        range.start,
        range.end,
        spots,
        fields,
        reportType.value as string,
        colors.value || []
      )

      minimapChart.setData(addData.data as ChartDataset<'line'>[])
      chart.setData(addData.data as ChartDataset<'line'>[])
    }

    minimapChart.updateZoomScale(center.value)
    chart.updateZoomScale(center.value)
  } catch (e) {
    console.log(e)
    configStore.startLoad()
    await initialize()
    configStore.endLoad()
  }
}

/**
 * 取得したデータを基にメインのチャートを描画
 */
const drawMainChart = (mainDatasets: ChartData, xRangeType: XRangeType) => {
  if (chart && chart.chartjs) {
    chart.destroy()
  }

  const canvas = myManySpotChartElement()
  const fields = currentFields.value as string[]
  const scales = props.field.items?.reduce((memo, item) => {
    memo[item.unit] = item.scale

    return memo
  }, {} as { [key: string]: Scale })

  chart = new IWSMainChart(canvas, fields, xRangeType, scales)
  chart.render(mainDatasets)
}

/**
 * ミニグラフのクリック
 */
const onClickMinimap = (newCenter: number) => {
  emit('update:center', newCenter)
  updateGraph()
}

/**
 * ミニグラフのパン
 */
const onPanCallback = throttle((newCenter: number) => {
  chart.updateZoomScale(newCenter)
}, 50)

/**
 * ミニグラフのパン完了
 */
const onPanCompleteCallback = throttle((newCenter: number) => {
  center.value = newCenter

  emit('update:center', center.value)
}, 100)

/**
 * ミニグラフの描画
 */
const drawMinimapChart = (minimapDatasets: ChartData, xRangeType: XRangeType) => {
  if (minimapChart && minimapChart.chartjs) {
    minimapChart.destroy()
  }

  const canvas = minimapChartElement()
  const fields = currentFields.value as string[]

  minimapChart = new IWSMiniChart(
    canvas,
    fields,
    xRangeType,
    onClickMinimap,
    onPanCallback,
    onPanCompleteCallback,
    setMainRangeVisual
  )
  minimapChart.render(minimapDatasets)
  minimapChart.updateZoomScale(center.value)
}

/**
 * 現時点でのメイングラフに表示されている最小時刻・最大時刻を返却
 * @returns
 */
const getMainRange = (_center: number) => {
  if (!chart) {
    return
  }

  return chart.getRange(_center)
}

/**
 * メインのグラフに描画するデータのみクロップ / 目印の+を表示するためのバブルチャートデータを挿入
 * @param graphData GraphData
 * @returns GraphData
 */
const setMainChartData = (graphData: GraphDatum[]): GraphData => {
  const xRangeType = reportType.value as string
  const { start: mainStart, end: mainEnd } = IWSMiniChart.getMarginRange(center.value, xRangeType)
  const start = moment(mainStart)
  const end = moment(mainEnd)
  const mainData: GraphDatum[] = []
  const labels = service.setLabels(start, end, xRangeType)
  const dataStyle = {
    pointRadius: 3,
    borderWidth: 2,
  }

  graphData.forEach(d => {
    mainData.push({ ...d, ...dataStyle })
  })

  const result: GraphData = {
    data: mainData,
    labels: labels,
  }

  return result
}

/**
 * myManySpotChartとmanySpotMinimapをつなぐ線をひく
 * @returns
 */
const drawLine = () => {
  if (
    !minimapChartElement() ||
    !myManySpotChartElement() ||
    !chart?.chartjs ||
    myManySpotChartElement().getBoundingClientRect().height < 10
  ) {
    return
  }
  const { top: manySpotMinimapTop } = minimapChartElement().getBoundingClientRect()
  const { bottom: canvasBottom } = myManySpotChartElement().getBoundingClientRect()
  const graphBottomToCanvasBottom = chart.chartjs.height - chart.chartjs.chartArea.bottom
  const line = document.getElementById('graphToGraphLine') as HTMLStyleElement
  const height = manySpotMinimapTop - canvasBottom + graphBottomToCanvasBottom - 25

  line.style.height = `${height}px`
  line.style.top = `-${height}px`
}

const mainRangeText = computed(() => {
  if (!chart) {
    return ''
  }

  const { start: mainMin, end: mainMax } = chart.getRange(center.value, props.field.reportType)
  const format =
    props.field.reportType === XRange.HOUR || props.field.reportType === XRange.DAY
      ? 'YYYY/M/D H:00'
      : props.field.reportType === XRange.MONTH
      ? 'YYYY/M/D'
      : 'YYYY/M'

  return `${moment(mainMin).format(format)} ~ ${moment(mainMax).format(format)}`
})

/**
 * ミニマップ内の太線で表示中箇所を囲む
 * @returns
 */
const setMainRangeVisual = () => {
  mainRange.value = chart.getRange(center.value)
  const { start: mainMin, end: mainMax } = chart.getRange(center.value)
  const { start: minimapStart, end: minimapEnd } = minimapChart.getRange(center.value)
  const canvas = minimapChart.canvas
  const ctx = minimapChart.ctx
  const minimapWidth = canvas.getBoundingClientRect().width
  const left = Math.floor(((mainMin - minimapStart) / (minimapEnd - minimapStart)) * minimapWidth)
  const right = Math.floor(((mainMax - minimapStart) / (minimapEnd - minimapStart)) * minimapWidth)
  const height = canvas.clientHeight

  if (!ctx || right - left > 500) {
    return
  }

  // 太線で囲む
  ctx.fillStyle = '#34495e'
  ctx.lineWidth = 6
  ctx.strokeRect(left, 0, right - left, height ? height * 5.2 : 200)
  ctx.restore()
}

const fixMinimapWidth = () => {
  if (!chart || !chart.chartjs) {
    return
  }

  // ミニマップの幅調整
  const { width, left } = chart.chartjs.chartArea
  const minimapWrapper = document.getElementsByClassName('minimap_wrapper')[0] as HTMLStyleElement

  minimapWrapper.style.width = `${width + 4}px`
  minimapWrapper.style.left = `${left}px`
  minimapChart.chartjs?.resize(width + 4, 40)
}

const adjustWindow = () => {
  fixMinimapWidth()
  drawLine()
}

const myManySpotChartElement = () => {
  return document.getElementById('myManySpotChart') as HTMLCanvasElement
}

const minimapChartElement = () => {
  return document.getElementById('minimap') as HTMLCanvasElement
}

const resetCenter = () => {
  const now = moment()
  switch (props.field.reportType) {
    case XRange.HOUR:
      center.value = moment(now)
        .set('hour', Math.floor(now.hour() / 8) * 8 + 4)
        .startOf('hour')
        .toDate()
        .getTime()
      break
    case XRange.DAY:
      center.value = moment(now).set('hour', 12).startOf('hour').toDate().getTime()
      break
    case XRange.MONTH:
      center.value = moment(now).set('date', 16).startOf('date').toDate().getTime()
      break
    case XRange.YEAR:
      center.value = moment(now).set('month', 6).startOf('month').toDate().getTime()
      break
  }

  emit('update:center', center.value)
}

watch(
  () => [props.stations, props.field, props.fields, currentStation.value],
  () => {
    nextTick(() => {
      initialize()
      resetCenter()
    })
  },
  {
    flush: 'post',
  }
)

watch(
  () => props.center,
  () => {
    center.value = props.center
  }
)

watch(
  () => center.value,
  (newValue, oldValue) => {
    if (!chart?.chartjs) {
      return
    }

    /*
     * カレンダー操作などで現在表示中の領域から外れた場合は初期化処理を行う
     */
    const oldRange = IWSMiniChart.getMarginRange(oldValue, props.field.reportType)
    const newRange = IWSMiniChart.getMarginRange(newValue, props.field.reportType)

    if (oldRange.start > newRange.end || oldRange.end < newRange.start) {
      return initialize()
    }

    minimapChart.getRange
    updateGraph()
  }
)

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _withDirectives(_createElementVNode("div", _hoisted_1, [
      (__props.showWindRateCount)
        ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
            (__props.field.stationId && __props.field.reportType && mainRange.value)
              ? (_openBlock(), _createBlock(WindRadar, {
                  key: 0,
                  stationId: __props.field.stationId,
                  startTime: mainRange.value.start,
                  endTime: mainRange.value.end,
                  type: __props.field.reportType
                }, null, 8, ["stationId", "startTime", "endTime", "type"]))
              : _createCommentVNode("", true)
          ]))
        : _createCommentVNode("", true),
      _withDirectives(_createElementVNode("div", _hoisted_3, _hoisted_6, 512), [
        [_vShow, !__props.showWindRateCount]
      ]),
      _withDirectives(_createElementVNode("div", _hoisted_7, [
        _withDirectives(_createElementVNode("div", _hoisted_8, _hoisted_10, 512), [
          [_vShow, initialized.value]
        ]),
        _hoisted_11,
        (initialized.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
              _createElementVNode("small", _hoisted_13, _toDisplayString(_unref(minimapStartText)), 1),
              _createElementVNode("small", _hoisted_14, _toDisplayString(_unref(minimapEndText)), 1),
              _createElementVNode("small", _hoisted_15, _toDisplayString(_unref(mainRangeText)), 1)
            ]))
          : _createCommentVNode("", true)
      ], 512), [
        [_vShow, initialized.value && !__props.showWindRateCount]
      ]),
      _hoisted_16
    ], 512), [
      [_vShow, !__props.modal]
    ]),
    (initialized.value)
      ? (_openBlock(), _createBlock(_Teleport, {
          key: 0,
          to: "#graphPrintHeader"
        }, [
          _createElementVNode("p", _hoisted_17, _toDisplayString(_unref(fieldRangeText)) + "グラフ", 1),
          _createElementVNode("p", _hoisted_18, _toDisplayString(_unref(fieldNameText) || _unref(currentStation)?.name), 1),
          _createElementVNode("p", _hoisted_19, _toDisplayString(_unref(mainRangeText)), 1)
        ]))
      : _createCommentVNode("", true)
  ], 64))
}
}

})